import { Form, Row, Col, Typography, Flex, Spin, Collapse, Image } from "antd";
import CustomInput from "../../../components/CustomInput/Input";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import { useSelector, useDispatch } from "react-redux";
import DealDetail from "../../../components/Details/DealDetail";
import { GLOBAL_CONSTANTS, ERROR_MSGS, BTN_TXT } from "../../../constants/constants";
import { capitalizeWords, formatCurrency, disputeAgeCalculation, reasonRules } from "../../../helpers/helpers";
import { formatPaymentDateTime } from "../helpers";
import { useState, useMemo } from "react";
import { CommentsRules } from "../../../helpers/helpers";
import FormFooter from "../../../components/CustomForms/FormFooter";
import ArrowDownIcon from "../../../assets/icons/arrow-down-icon.svg"
import CustomTable from "../../../components/CustomTable/CustomTable";
import { getDisputeCommentColumns, getEscalationCommentColumns } from "./getPaymentColumns";
import { addEscalation, getTxnDetails, addEscalationCommentsById } from "../../../redux/slices/PaymentsSlice";

const { Text } = Typography

const EscalateForm = ({ onClose }) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch()
    const { disputeDetails, escalationLoading, disputeLoading, escalationDetails, error, escalationSubmitLoading } = useSelector((state) => state.payments);
    const { userList } = useSelector((state) => state?.user);
    const [activeKey, setActiveKey] = useState(["1"])
    const hasEscalationDetails = disputeDetails?.DisputeEscalationDetail?.escalationId;

    const panelStyle = {
        marginBottom: 24,
        borderRadius: 8,
        border: "1px solid #D8E4FD",
    };

    const disCommentsColumns = useMemo(
        () =>
            getDisputeCommentColumns(),
        []
    );

    const escCommentsColumns = useMemo(
        () =>
            getEscalationCommentColumns(),
        []
    );

    const collapseItems = [
        {
            key: '1',
            label: <Text strong>Dispute Comment History</Text>,
            children: <CustomTable
                rowKey="disputeCommentId"
                columns={disCommentsColumns}
                dataSource={disputeDetails?.disputeCommentsDetails || []}
                tableContainerStyle='dispute-comments-table'
            />,
            style: { ...panelStyle },
        },
        ...(hasEscalationDetails
            ? [
                {
                    key: '2',
                    label: <Text strong>Escalation Comment History</Text>,
                    children: <CustomTable
                        rowKey="escalationCommentId"
                        columns={escCommentsColumns}
                        dataSource={escalationDetails?.escalationCommentsDetail || []}
                        tableContainerStyle='dispute-comments-table'
                    />,
                    style: { ...panelStyle },
                },
            ]
            : []),
    ];

    const handleSubmit = async (values) => {
        let payload, action;

        if (hasEscalationDetails) {
            payload = {
                escalationId: escalationDetails.escalationId,
                escalationComments: values.reason,
            }
            action = addEscalationCommentsById
        } else {
            payload = {
                disputeId: disputeDetails.disputeId,
                escalationDate: new Date().toISOString(),
                escalationUserId: values.userId,
                escalationComments: values.comments,
            };
            action = addEscalation
        }
        const res = await dispatch(action(payload));
        if (res.payload.statusCode === 201) {
            onClose();
            dispatch(getTxnDetails(disputeDetails?.transactionId));
        }
    }

    return (
        <div className="raise-dispute-form-container">
            <Spin spinning={disputeLoading || escalationLoading}>
                {hasEscalationDetails && (
                    <>
                        <div className="dispute-escalation-details-container">
                            <Row gutter={[32, 16]}>
                                <Col xs={24} md={12}>
                                    <Flex gap={15} vertical >
                                        <DealDetail
                                            label="Escalation ID"
                                            value={escalationDetails?.escalationId || GLOBAL_CONSTANTS.NA}
                                        />
                                        <DealDetail
                                            label="Escalation User Name"
                                            value={escalationDetails?.createdByName || GLOBAL_CONSTANTS.NA}
                                        />
                                        <DealDetail
                                            label="Escalated to Username"
                                            value={escalationDetails.escalationUsername || GLOBAL_CONSTANTS.NA}
                                        />
                                    </Flex>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Flex gap={15} vertical>
                                        <DealDetail
                                            label="Escalation Date"
                                            value={formatPaymentDateTime(escalationDetails?.escalationDate) || GLOBAL_CONSTANTS.NA}
                                        />
                                        <DealDetail
                                            label="Escalation User Email"
                                            value={escalationDetails.createdUseremail || GLOBAL_CONSTANTS.NA}
                                        />
                                        <DealDetail
                                            label="Escalated to User Email"
                                            value={escalationDetails.escalationUseremail || GLOBAL_CONSTANTS.NA}
                                        />
                                    </Flex>
                                </Col>
                            </Row>
                        </div>
                        <div className="dispute-escalation-details-container">
                            <DealDetail
                                label="Escalation Reason"
                                value={escalationDetails?.escalationCommentsDetail?.length > 0 ? escalationDetails?.escalationCommentsDetail[0]
                                    ?.escalationComment : GLOBAL_CONSTANTS.NA}
                            />
                        </div>
                    </>
                )}

                <div className="dispute-escalation-details-container">
                    <Row gutter={[32, 16]}>
                        <Col xs={24} md={12}>
                            <Flex gap={15} vertical >
                                <DealDetail
                                    label="Dispute ID"
                                    value={disputeDetails?.disputeId || GLOBAL_CONSTANTS.NA}
                                />
                                <DealDetail
                                    label="Dispute Type"
                                    value={disputeDetails?.disputeTypeDesc || GLOBAL_CONSTANTS.NA}
                                />
                                <DealDetail
                                    label="Dispute Reason"
                                    value={disputeDetails?.disputeReasonDesc || GLOBAL_CONSTANTS.NA}
                                />
                            </Flex>
                        </Col>
                        <Col xs={24} md={12}>
                            <Flex gap={15} vertical>
                                <DealDetail
                                    label="Dispute age"
                                    value={`${disputeAgeCalculation(disputeDetails?.disputeDate)} day(s)` || GLOBAL_CONSTANTS.NA}
                                />
                                <DealDetail
                                    label="Dispute Priority"
                                    value={disputeDetails?.disputePriorityDesc || GLOBAL_CONSTANTS.NA}
                                />
                                <DealDetail
                                    label="Dispute Amount"
                                    value={formatCurrency(disputeDetails?.disputeAmount) || GLOBAL_CONSTANTS.NA}
                                />
                            </Flex>
                        </Col>
                    </Row>
                </div>
            </Spin>

            <Form
                layout="vertical"
                form={form}
                onFinish={handleSubmit}
                initialValues={{ remember: true, disputeAge: 0 }}
            >
                <Row gutter={[24, 0]}>
                    {hasEscalationDetails ? (
                        <Col xs={24}>
                            <CustomInput
                                label="Add New Escalation Reason"
                                name="reason"
                                rows={3}
                                isRequired={true}
                                isTextArea={true}
                                type="text"
                                placeholder="Enter Reason"
                                rules={reasonRules}
                            />
                        </Col>
                    ) : (
                        <>
                            <Col xs={24}>
                                <CustomSelect
                                    label="Escalate to User"
                                    name="userId"
                                    showSearch
                                    isRequired={true}
                                    placeholder="Select User"
                                    options={userList?.map((user) => ({
                                        value: user.userId,
                                        label: capitalizeWords(user.userName),
                                    }))}
                                    rules={[{ required: true, message: ERROR_MSGS.SEL_USER_ERR_MSG }]}
                                />
                            </Col>
                            <Col xs={24}>
                                <CustomInput
                                    label="Escalation Comment"
                                    name="comments"
                                    rows={3}
                                    isRequired={true}
                                    isTextArea={true}
                                    type="text"
                                    placeholder="Enter your Comments"
                                    rules={CommentsRules}
                                />
                            </Col>
                        </>
                    )}

                </Row>

                <Collapse
                    bordered={false}
                    activeKey={activeKey}
                    onChange={(key) => setActiveKey(key)}
                    expandIcon={({ isActive }) => (
                        <Image
                            src={ArrowDownIcon}
                            alt='Arrow'
                            preview={false}
                            style={{
                                transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease'
                            }}
                        />
                    )}
                    expandIconPosition='end'
                    style={{ width: '100%' }}
                    items={collapseItems}
                />

                <FormFooter
                    onCancelClick={() => {
                        onClose();
                        form.resetFields();
                    }}
                    submitLoading={escalationSubmitLoading}
                    btnName={BTN_TXT.SUBMIT}
                    error={error}
                />

            </Form >
        </div >
    )
}

export default EscalateForm